import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { MaxBrainAudience } from 'app/library/audience/models/audience.model';
import { ModuleUser } from 'app/library/module-user/models/module-user.model';
import { MaxBrainAuthState } from 'app/projects/auth/src/lib/models/auth.state';
import { IMobilePostMessage } from 'app/projects/shared/src/lib/interfaces/mobile-post-message.interface';
import { environment } from 'environments/environment';
import { BehaviorSubject } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { AnalyticsService } from './analytics.service';
import { IdleTrackerService } from './idle-tracker.service';
import { SharedAnalyticsService } from './shared-analytics.service';

interface IPageVisitEventData {
    url: string;
    session_identifier: string;
    access_token: string;
    window_size: { width: string; height: string };
}

export interface IContextListItem {
    type: string;
    name: string;
    id?: string;
    edit_mode?: boolean;
    module_user?: ModuleUser;
    module_type?: 'e-learning' | 'private' | 'default';
    section_type?: string;
    node_path?: string;
}

interface ILocationDurationEventData {
    url: string;
    context: IContextListItem[];
    session_identifier: string;
    session_start_timestamp: number;
}

@Injectable()
export class GeneralAnalyticsService extends AnalyticsService {
    private _currentDurationEventData: ILocationDurationEventData = null;
    private _event: NavigationEnd;

    get currentDurationEventData(): ILocationDurationEventData {
        return this._currentDurationEventData;
    }

    private startTimestamp$ = new BehaviorSubject(0);

    private _isIdleTrackingEnabled: boolean = true;
    private _isPageActive = false;

    private _toggleIdleTracking(): void {
        this._isIdleTrackingEnabled = !this._isIdleTrackingEnabled;
    }

    sendLocationDurationContainer: ($event: BeforeUnloadEvent) => void;

    private _storeCurrentDurationEventData(): void {
        const context = this._sharedAnalyticsService.generateLocationContext(this._event.urlAfterRedirects.split('/').slice(1));

        this._currentDurationEventData = {
            url: this._event.urlAfterRedirects,
            context,
            session_identifier: localStorage.getItem('session_identifier'),
            session_start_timestamp: Date.now(),
        };
        this._isPageActive = true;
    }

    private _isScormLearningPage(): boolean {
        return !!this._route.snapshot.queryParamMap.get('scormLearning');
    }

    private async _onPageLeave(): Promise<void> {
        if (!this._isPageActive || !this._event) {
            return;
        }

        this._isPageActive = false;
        await this._sendLocationDuration('location_duration');
    }

    private _onPageReturn(): void {
        if (this._isPageActive || !this._event) {
            return;
        }

        this._onNavigationStartEvent();
        this._storeCurrentDurationEventData();
    }

    constructor(
        http: HttpClient,
        zone: NgZone,
        private _router: Router,
        private _route: ActivatedRoute,
        private _store: Store,
        private _idleTrackerService: IdleTrackerService,
        private _sharedAnalyticsService: SharedAnalyticsService
    ) {
        super(http);

        zone.runOutsideAngular(() => {
            this.sendLocationDurationContainer = ($event) => this._sendLocationDuration('location_duration');
            window.addEventListener('beforeunload', this.sendLocationDurationContainer.bind(this), true);

            document.addEventListener('visibilitychange', () => {
                if (!this._event) {
                    return;
                }

                if (document.hidden) {
                    void this._onPageLeave();
                } else {
                    this._onPageReturn();
                }
            });

            window.addEventListener('blur', () => {
                if (!this._event || document.hidden || this._isScormLearningPage()) {
                    return;
                }

                void this._onPageLeave();
            });

            window.addEventListener('focus', () => {
                if (!this._event || document.hidden || this._isScormLearningPage()) {
                    return;
                }

                this._onPageReturn();
            });
        });

        this._idleTrackerService.userIdlenessChecker.subscribe((isIdle) => {
            if (isIdle && this._isIdleTrackingEnabled) {
                this._sendLocationDuration('idle_timeout');
                this._sendLocationDuration('location_duration');
            } else if (this.currentDurationEventData) {
                // We don't want to stop tracking location duration if user is on Scorm learning page
                if (!this._isScormLearningPage() && this.currentDurationEventData?.session_start_timestamp) {
                    this.currentDurationEventData.session_start_timestamp = Date.now();
                }
            }
        });

        this._router.events.subscribe(async (event) => {
            if (event instanceof NavigationStart) {
                this._onNavigationStartEvent();
            }

            if (event instanceof NavigationEnd) {
                this._event = event;

                try {
                    // removes refresh token param from url
                    const urlParams = new URLSearchParams(window.location.search);
                    const refresh_token = urlParams.get('refresh_token');
                    if (refresh_token) {
                        let url = new URL(window.location.href);
                        url.searchParams.delete('refresh_token');
                        window.history.replaceState(null, document.title, url.toString());
                    }

                    const isReady = await this._checkRedirection(Date.now());

                    if (isReady) {
                        await this._sendLocationDuration('location_duration');

                        this._storeCurrentDurationEventData();

                        await this._createPageVisitEvent('page_visit', event.urlAfterRedirects);
                    }
                } catch (e) {
                    if (!environment.production) {
                        console.warn(e, event.urlAfterRedirects);
                    }
                }
            }
        });

    }

    async onResumeWebApp(data: IMobilePostMessage) {
        this._toggleIdleTracking();
        this._onNavigationStartEvent();

        this._storeCurrentDurationEventData();

        await this._createPageVisitEvent('page_visit', this._event.urlAfterRedirects, data);
    }

    private _onNavigationStartEvent(): void {
        this._idleTrackerService.lastActionTimestamp = Date.now();
        localStorage.setItem('session_identifier', uuidv4());

        this.startTimestamp$.next(Date.now());
    }

    preventAppFromSendingEvents(): void {
        this._toggleIdleTracking();
        this._sendLocationDuration('location_duration');
    }

    private _sleep(ms: number) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    async _checkRedirection(endTime: number): Promise<boolean> {
        let start: number = 0;

        this.startTimestamp$.subscribe((value) => {
            start = value;
        });

        await this._sleep(100);

        if (start - endTime < 0) {
            return true;
        }
        return false;
    }

    private _createPageVisitEvent(eventName: string, url: string, data?: IMobilePostMessage): Promise<any> {
        let eventContent: IPageVisitEventData = {
            url: url,
            session_identifier: localStorage.getItem('session_identifier'),
            ...this.currentDurationEventData,
            access_token: data?.payload?.appSessionId ? data.payload.appSessionId : this._store.selectSnapshot(MaxBrainAuthState.getAccessToken),
            window_size: data?.payload?.window_size ? data.payload.window_size : { width: '', height: '' },
        };

        return this._sendEvent(eventName, eventContent);
    }

    private _sendLocationDuration(event_type: 'idle_timeout' | 'location_duration'): Promise<any> {
        if (this._currentDurationEventData) {
            const session_end_timestamp = Date.now();
            const content: { access_token: string; session_end_timestamp: number; duration: number; idleDuration?: number } & ILocationDurationEventData = {
                access_token: this._store.selectSnapshot(MaxBrainAuthState.getAccessToken),
                session_end_timestamp,
                duration: session_end_timestamp - this.currentDurationEventData?.session_start_timestamp,
                ...this.currentDurationEventData,
            };

            switch (event_type) {
                case 'idle_timeout':
                    content.idleDuration = IdleTrackerService.USER_IDLE_TIMER_VALUE_IN_MIN;
                    break;
                default:
                    this._currentDurationEventData = null;
                    break;
            }

            return this._sendEvent(event_type, content);
        }
    }

    createPostingEvent(eventName: string, postingId: string, postingTypeId: string, postingName: string, audience: MaxBrainAudience, widgetId: string, widgetName: string): void {
        const session_end_timestamp = Date.now();
        let eventContent: any = {
            access_token: this._store.selectSnapshot(MaxBrainAuthState.getAccessToken),
            duration: session_end_timestamp - this.currentDurationEventData?.session_start_timestamp,
            ...this.currentDurationEventData,
            posting_id: postingId,
            posting_type_id: postingTypeId,
            posting_title: postingName,
            widget_name: widgetName,
            widget_id: widgetId,
        };
        if (audience) {
            eventContent.audience_name = audience.name;
            eventContent.audience_id = audience.id;
        }
        this._sendEvent(eventName, eventContent);
    }

    createPostingSearchEvent(eventName: string, searchTerm: string): void {
        let eventContent: any = {
            search_term: searchTerm,
            session_identifier: localStorage.getItem('session_identifier'),
            ...this.currentDurationEventData,
            access_token: this._store.selectSnapshot(MaxBrainAuthState.getAccessToken),
        };

        this._sendEvent(eventName, eventContent);
    }

    createAlumniWidgetEvent(eventName: string, userId: string, widgetId: string, widgetName: string): void {
        const session_end_timestamp = Date.now();
        let eventContent: any = {
            access_token: this._store.selectSnapshot(MaxBrainAuthState.getAccessToken),
            duration: session_end_timestamp - this.currentDurationEventData?.session_start_timestamp,
            ...this.currentDurationEventData,
            user_id: userId,
            widget_id: widgetId,
            widget_name: widgetName,
        };

        this._sendEvent(eventName, eventContent);
    }
}
